// The fixed list of permissions (PLAN.md §7). Roles are configurable bundles of these.
// Every API route declares which it needs; the server checks them on every request.
//
// The database keeps a copy in known_permissions, so it rejects unknown strings too
// (ADR 0011). To add one, also add only your new strings in your slice's migration:
//   SELECT app_add_known_permissions(ARRAY['module.thing.verb']);
// A test fails if this list and the table differ, in either direction.
// Give it to the standard roles in role-templates.ts (ADR 0031), never only in the seed:
// existing organisations are offered what the templates gain.
// Phase 0 administration, then Phase 1 registration and visits and finance master data;
// later slices add their own.

import { roleNeedsTwoFactor } from './two-factor.ts';

export const PERMISSIONS = {
  'admin.users.read': 'See users and the roles they hold',
  'admin.users.manage': 'Create users, disable them and change their roles',
  'admin.roles.manage': 'Create roles and change which permissions they grant',
  'admin.facilities.manage': 'Add facilities and branches and change their details',
  'admin.organisation.manage': 'Change organisation settings, such as the session timeout',
  // Account security (docs/plans/golive-onboarding-and-account-security.md §3).
  'admin.users.password_reset':
    'Issue a one-time password reset link to a user, to hand over in person',
  'admin.users.two_factor_reset.request':
    "Ask for a user's two-factor sign-in to be reset, giving a reason",
  'admin.users.two_factor_reset.approve':
    'Approve or reject two-factor resets asked for by someone else',
  'audit.read': 'Read the audit log',
  'errors.read': 'See application error reports, to support staff and fix problems',
  'integrations.read': 'See the state of outbound integrations and their message log',
  'patients.read': 'Find patients and see their records',
  'patients.register': 'Register new patients and dependants',
  'patients.update': 'Correct patient details, identifiers, contacts, relationships and consent',
  'patients.import':
    'Import patients from another system’s records; only for roles that use two-factor sign-in',
  'visits.read': "See a patient's visits",
  'visits.open': 'Check patients in and open visits',
  'visits.cancel': 'Cancel a visit opened in error, with a reason',
  'records.break_glass':
    'Open a sensitive record their role is not cleared for, giving a reason; always audited',
  // Terminology and imports (docs/plans/phase1-terminology-and-import.md §3.1).
  'terminology.read': 'Search and look up diagnosis, lab and service codes',
  'terminology.manage': 'Import code system versions and choose which version is active',
  'imports.read': 'See imports, their problems and reports',
  // Finance master data (docs/plans/phase1-finance-catalogue.md §3.1).
  'catalogue.read': 'See services, items, prices, payers and schemes',
  'catalogue.manage': 'Add and change services and items, their codes and where they are offered',
  'prices.manage': 'Create price lists, change prices, assign lists to payers and import prices',
  'payers.manage': 'Add and change payers, schemes and who-pays-what rules',
  'stock.read': 'See stock balances, stock cards, batches, and expiry and reorder lists',
  'stock.receive': 'Record and post goods received notes',
  'stock.transfer': 'Transfer stock between stores',
  'stock.count': 'Start stock counts and record what was found',
  'stock.adjust.request': 'Request stock adjustments and opening balances',
  'stock.adjust.approve':
    'Approve or reject stock adjustments, opening balances, count variances and receipt reversals',
  'stock.manage': 'Set up stores, suppliers and reorder levels',
  // Procurement (docs/plans/phase2-procurement.md §3.1).
  'stock.requisition': 'Ask another store for stock (store requisitions)',
  'procurement.read': 'See purchase requisitions, LPOs, supplier invoices and supplier statements',
  'procurement.requisition': 'Raise purchase requisitions',
  'procurement.order': 'Prepare LPOs, cancel them, and close them short',
  'procurement.approve': 'Approve or reject purchase requisitions and LPOs, with a reason',
  'procurement.invoice': 'Record supplier invoices against LPOs',
  'procurement.invoice.approve':
    "Approve or reject supplier invoices, giving a reason when they don't match",
  // Queues and triage (docs/plans/phase1-triage.md §3.1).
  'queues.read': 'See the patients waiting at service points',
  'queues.manage': 'Call, start, finish, return, remove and send patients between queues',
  'clinical.read': 'See vitals, triage and allergy records',
  'triage.record': 'Record vitals, complaint and triage category, and correct them',
  'allergies.record':
    'Record allergies and "No known allergies", and mark them entered in error (nurses and clinicians)',
  'clinical_rules.manage':
    'Change vital limits, reference ranges, critical ranges, triage categories and codings',
  'clinical_rules.approve': 'Approve clinical rules another person changed',
  // Billing and the cashier (docs/plans/phase1-billing.md §3.1).
  'billing.read': 'See bills, charges, invoices, payments and receipts',
  'billing.charge.manual': 'Add a charge by hand, with a reason',
  'billing.charge.cancel': 'Cancel a charge that is not yet on a bill, with a reason',
  'billing.waiver.request': 'Ask for a waiver or discount on what a patient owes',
  'billing.waiver.approve': 'Approve or reject waivers and discounts asked for by someone else',
  'billing.credit_note.request': 'Ask for a credit note against an invoice',
  'billing.credit_note.approve': 'Approve or reject credit notes asked for by someone else',
  'billing.settings.manage':
    'Choose what must be paid before service and which service points charge a consultation fee',
  'payments.take': 'Take payments, issue bills and print receipts, during an open shift',
  'payments.refund.request': 'Ask for a refund',
  'payments.refund.approve': 'Approve or reject refunds asked for by someone else',
  // Paybill (M-Pesa C2B) payments (ADR 0020).
  'payments.c2b.match':
    'Match a paybill payment that could not be matched automatically to a bill, with a reason',
  'cashier.shift.open': 'Open a cashier shift with a float',
  'cashier.shift.close': 'Close their own cashier shift with a cash count',
  'cashier.shift.review': "See every cashier's shifts and close an abandoned one, with a reason",
  // eTIMS signing modes (docs/plans/phase3-etims-vscu.md §3; ADR 0041).
  'etims.settings.manage':
    'Choose whether this facility signs tax documents through eTIMS OSCU or VSCU, or not at all, with a reason',
  'etims.documents.sign_again': 'Sign again a tax document whose eTIMS signing failed',
  // The lab (docs/plans/phase1-lab.md §3.1). Ranges use clinical_rules.* from triage.
  'lab.catalogue.manage': 'Set up lab sections, analytes, tests and their components',
  'lab.read': 'See the lab worklist, samples and results in progress',
  'lab.order': 'Request lab tests for a visit',
  'lab.order.cancel': 'Cancel a lab request, with a reason',
  'lab.collect': 'Collect, receive and reject samples and print their labels',
  'lab.result.enter': 'Enter and save lab results',
  'lab.result.validate': 'Validate lab results (technical validation, or the only stage)',
  'lab.result.authorise': 'Give clinical validation where the facility uses two stages',
  'lab.result.correct': 'Correct a released lab report or mark it entered in error',
  'lab.results.read': "See released lab results in a patient's record",
  'lab.report.print': 'Print lab reports',
  'lab.critical.escalations':
    'Receive critical lab results nobody has acknowledged, and record phone read-backs',
  'lab.settings.manage': 'Change lab settings for a facility',
  // Lab analyzers (docs/plans/phase2-lab-connector.md §3.6; ADR 0026).
  'lab.analyzers.manage':
    'Add lab analyzers, rotate their credentials, set their options and map their test codes',
  'lab.analyzers.review': 'Apply or dismiss analyzer results waiting for review',
  // Lab quality control (docs/plans/phase3-lab-qc-and-microbiology.md §2; ADR 0039).
  'lab.qc.read': 'See QC materials, runs, Levey-Jennings charts and QC holds',
  'lab.qc.record': 'Record QC runs and corrective actions, or mark a run entered in error',
  'lab.qc.manage': 'Set up QC materials, lots, target means and SDs, and the Westgard rules in use',
  'lab.qc.override':
    'Request, or approve as a second person, an override of a rejected QC run so results can be validated',
  // The pharmacy (docs/plans/phase1-dispensing.md §5.1).
  'pharmacy.read':
    'See the prescription queue, prescriptions sent to the pharmacy, sales and dispensing records',
  'pharmacy.dispense':
    'Prepare and dispense prescriptions, print labels, record counselling and close balances',
  'pharmacy.substitute':
    'Give a different product than the one prescribed, with a reason, where the prescriber allows it',
  'pharmacy.sell': 'Sell over-the-counter items at the pharmacy counter',
  'pharmacy.return.request': 'Ask to take back medicines that were dispensed or sold',
  'pharmacy.return.approve': 'Approve or reject returns asked for by someone else',
  'pharmacy.supply_class.manage': 'Mark stock items as prescription-only or over-the-counter',
  // Consultation, orders and procedures (docs/plans/phase1-consultation.md §3.1). The
  // prescribing slice (C3) adds its own.
  'consultation.record':
    'Start and finish consultations, write and sign notes, record diagnoses and problems',
  'consultation.amend_any':
    'Amend a signed note written by someone else, with a reason; in no role by default',
  'allergies.verify': 'Confirm, refute, resolve or recode an allergy (clinicians)',
  'procedures.order': 'Request procedures for a visit',
  'procedures.perform': 'Record a procedure as done and write its note',
  'procedures.cancel': 'Cancel a procedure request, with a reason',
  'imaging.order': 'Request imaging for a visit, and cancel a request with a reason',
  'imaging.perform': 'Mark imaging as done, with a note and a link to the images',
  // Radiology RIS lite (docs/plans/phase2-radiology.md §3.1).
  'imaging.read': 'See the imaging worklist and exams in progress',
  'imaging.schedule': 'Schedule imaging requests and change the time',
  'imaging.report.write': 'Write and sign imaging reports, and amend them with a reason',
  'imaging.report.verify': 'Verify imaging reports',
  'imaging.results.read': "See imaging reports in a patient's record and the consultation",
  'imaging.report.print': 'Print imaging reports',
  'imaging.catalogue.manage':
    'Set the modality, patient preparation and report template of imaging tests',
  'note_templates.manage': 'Change consultation note templates',
  'visits.close': 'Close a visit when the patient has finished',
  // Reports (docs/plans/phase1-reports.md §7.1). Registers list patients; the lab register
  // also needs lab.read. Printing uses the report's read permission; downloading needs
  // reports.export as well.
  'reports.summary.read': "See the owner's day summary",
  'reports.finance.read': 'See daily collections and revenue reports',
  'reports.claims.read': 'See payer share not yet claimed and, later, the claims pipeline',
  'reports.registers.read': 'See the OPD, lab and pharmacy registers, which list patients',
  'reports.moh.read': 'Prepare and review MOH returns and see diagnoses that could not be placed',
  'reports.moh.approve':
    'Approve an MOH return for submission and record that it was entered in KHIS',
  'reports.export': 'Download reports as CSV files',
  'reports.settings.manage': 'Map the facility’s service points and items to MOH 717 rows',
  // KHIS submissions and quality indicators (docs/plans/phase3-khis-and-quality.md §2).
  'khis.read': 'See KHIS submissions, their state and the KHIS mapping',
  'khis.submission.prepare':
    'Prepare a month’s figures for KHIS, prepare a correction, and discard a prepared month',
  'khis.submission.approve':
    'Approve a month’s figures someone else prepared, which sends them to KHIS, and send again after a failure',
  'khis.mapping.manage':
    'Set the facility’s KHIS organisation unit and edit and activate the KHIS mapping',
  'quality.read': 'See the quality indicators dashboard',
  'quality.settings.manage': 'Set the facility’s own targets for quality indicators',
  // Deeper analytics (docs/plans/phase3-deeper-analytics.md A2). Each area also needs the
  // permission its data already needs; lists behind a figure need the registers too.
  'analytics.read': 'See the analytics dashboards: trends and breakdowns over a period',
  // Prescribing and the formulary (docs/plans/phase1-consultation.md §3.1). Dose rules and
  // group memberships use clinical_rules.* from triage.
  'prescriptions.write': 'Sign prescriptions, and stop, cancel or correct their lines',
  'prescriptions.read': 'See prescriptions',
  'formulary.read': 'Search the formulary',
  'formulary.manage':
    'Add and change formulary items, routes, ingredients, frequencies and group memberships',
  // SHA: eligibility, registries, pre-authorisation and claims (docs/plans/phase1-sha.md §8.1).
  'sha.eligibility.check': "Check a patient's SHA cover and record a check made on the SHA portal",
  'sha.visit.manage':
    'Start an SHA visit again after a failure, or convert an SHA line to cash with a reason',
  'registries.search': 'Search the national facility and health worker registries',
  'registries.link': 'Link this facility and practitioners to their national registry records',
  'preauth.read': 'See pre-authorisation requests and their state',
  'preauth.request': 'Ask SHA to approve a service',
  'preauth.override':
    'Go ahead without an SHA approval that could not be obtained, giving a reason',
  'preauth_list.manage': 'Import and activate the pre-authorisation list',
  'claims.read': 'See claims, their lines and history',
  'claims.submit': 'Prepare, submit and resubmit claims, and take claims to fix',
  'claims.write_off.request': 'Ask to write off a claim that cannot be paid',
  'claims.write_off.approve': 'Approve or reject claim write-offs asked for by someone else',
  'sha.settings.manage': 'Change SHA settings for a facility',
  // The local practitioner profile (docs/plans/phase1-consultation.md §3.1, §2.13).
  'practitioners.manage':
    "Keep practitioners' cadre and licence details, and when licences show as expiring",
  // Staff records with licence expiry (docs/plans/phase2-staff-records.md §3).
  'staff.read':
    'See staff records, licence copies and the list of licences that have expired or expire soon',
  'staff.manage':
    'Change staff employment details, upload licence copies and mark licence notices as seen',
  // Clinical documents (docs/plans/phase1-consultation.md §3.1, C4).
  'sick_sheets.issue': 'Issue sick sheets, and mark one entered in error with a reason',
  'referrals.write':
    'Refer patients to other facilities with a letter, write feedback letters to referring facilities, and mark either entered in error with a reason',
  'documents.read': "See, open and download a patient's uploaded documents",
  'documents.upload': 'Upload documents to a visit, and mark one entered in error with a reason',
  // Appointments and SMS reminders (docs/plans/phase2-appointments.md §3.1).
  'appointments.read': "See appointment lists, the day view and a patient's appointments",
  'appointments.book':
    'Book, reschedule and cancel appointments, mark no-shows, and record whether a patient wants SMS reminders',
  'appointments.schedules.manage': 'Set up clinic and practitioner schedules for appointments',
  // Private insurance, manual workflow (docs/plans/phase2-private-insurance.md §4.1).
  'insurance.coverage.manage': "Record, end and correct patients' insurance cover",
  'insurance.preauth.manage': "Record approval requests to insurers and the insurers' answers",
  'insurance.claims.read': 'See insurer claims, invoices, payments and debtors',
  'insurance.claims.manage':
    'Prepare and submit insurer claims and invoices, and record rejections',
  'insurance.remittance.record': 'Record insurer payments and allocate them to claims',
  'insurance.write_off.request': 'Ask to write off what an insurer will not pay',
  'insurance.write_off.approve': 'Approve or reject insurer write-offs asked for by someone else',
  // Inpatient ADT and the bed board (docs/plans/phase2-inpatient-adt.md §3.1).
  'inpatient.read': 'See the bed board, admissions and where inpatients are',
  'inpatient.admit': 'Admit patients from a visit',
  'inpatient.move': 'Give inpatients a bed and move them between beds and wards',
  'inpatient.discharge': 'Discharge inpatients and write the discharge summary',
  'inpatient.admission.cancel': 'Cancel an admission entered in error, with a reason',
  'inpatient.beds.manage': 'Mark beds clean, and block and unblock them with a reason',
  'inpatient.wards.manage': 'Set up wards, rooms and beds and the bed-day charge',
  // Inpatient billing (docs/plans/phase2-inpatient-billing.md §3.1).
  'billing.packages.manage': 'Set up packages: what they are charged as and what they cover',
  'inpatient.package.apply':
    'Apply a package to an inpatient stay, and remove one applied in error, with a reason',
  // Ward rounds and nursing (docs/plans/phase2-ward-rounds-nursing.md §3.1). Ward rounds
  // use consultation.record; reading the chart uses clinical.read.
  'nursing.record': 'Record vitals rounds, nursing notes and ward handover notes for inpatients',
  // eMAR and inpatient pharmacy (docs/plans/phase2-emar-inpatient-pharmacy.md §3.1). Reading
  // the medicines chart uses inpatient.read and clinical.read; ward orders use pharmacy.read.
  'medication.administer':
    'Record medicine doses given, held, refused or missed for inpatients, and enter them in error',
  'pharmacy.verify': 'Verify inpatient medication orders and choose ward stock or patient supply',
  'emar.settings.manage': 'Set the early and late windows for medicine doses on the wards',
  // Medical records and data-subject rights (docs/plans/phase2-records-data-rights.md §3.1).
  'records.requests.read':
    'See data-subject requests (access, correction, objection, restriction, deletion) and the records-release log',
  'records.requests.manage':
    'Log data-subject requests, record identity checks, link corrections and close requests with a response',
  'records.export':
    "Export or print a patient's record for a verified access request, giving a recipient and a reason",
  'records.access_log.read': "See who viewed, changed, printed or exported a patient's record",
  // Chart tracking (docs/plans/phase2-medical-records-charts.md §5).
  'records.charts.read': "See patients' charts, where each is now, its history and overdue charts",
  'records.charts.manage': 'Open a chart for a patient, and issue and return charts',
  'records.settings.manage': 'Set the records-room locations and the chart return period',
  // Privacy tooling (docs/plans/golive-privacy-tooling.md §6).
  'sensitivity_labels.manage':
    'Create and change sensitivity labels and choose which roles are cleared for each',
  'visits.label': 'Put a sensitivity label their role is cleared for on a visit, giving a reason',
  'visits.unlabel': 'Take a sensitivity label off a visit, giving a reason',
  'records.break_glass.review': 'See every break-glass opening of a record and mark each reviewed',
  'records.corrections.review': 'Accept or decline a request to correct a patient’s record',
  'privacy.retention.manage': 'See and change how long system data is kept before it is purged',
  // Theatre lite (docs/plans/phase2-theatre.md §3.1).
  'theatre.read': 'See the theatre list, theatre cases, their checklists and operation notes',
  'theatre.book': 'Book, reschedule and cancel theatre cases',
  'theatre.checklist.record':
    'Record the pre-op checklist and the safety checklist sign in, time out and sign out',
  'theatre.note.write':
    'Write, sign and amend operation notes; people who hold it can be chosen as surgeon',
  'theatre.charges.record':
    'Record theatre services used in a case, which are charged to the visit, and cancel one recorded in error',
  'theatre.settings.manage':
    'Set up theatres and their fee, and the safety checklist items and their wording',
  // Casualty and unknown patients (docs/plans/phase2-casualty.md §3.1).
  'casualty.register':
    'Register an unidentified patient in casualty with a temporary label, opening their emergency visit',
  'patients.merge.request':
    "Ask to merge an unidentified patient's record into the identified patient's record",
  'patients.merge.approve':
    "Approve or reject merging an unidentified patient's record into another patient's",
  // Referrals (docs/plans/phase2-referrals.md §3.1).
  'referrals.read': 'See the referrals list and referral records, sent and received',
  'referrals.track':
    'Record what happened to a referral: acknowledged, attended, feedback received, closed, or a note',
  'referrals.incoming.record':
    "Log referrals received at reception on the patient's visit, and mark one entered in error with a reason",
  'referrals.export': 'Download the referrals list as a CSV file, which lists patient names',
  // Death and birth notification, and the mortuary
  // (docs/plans/phase2-mortuary-and-notifications.md §3.1).
  'deaths.read': 'See death records, the death register and print death notification summaries',
  'deaths.record': 'Record a death, with the certifying clinician, causes and manner',
  'deaths.correct.request': 'Ask for a death record to be marked entered in error, with a reason',
  'deaths.correct.approve': 'Approve or reject marking a death record entered in error',
  'mortuary.read': 'See the mortuary, its cold rooms and cases',
  'mortuary.intake': 'Receive bodies into the mortuary and move them between slots',
  'mortuary.release.request': 'Record who is collecting a body, after checking their identity',
  'mortuary.release.approve': 'Approve or reject releasing a body to the person collecting it',
  'mortuary.case.cancel': 'Cancel a mortuary case entered in error, with a reason',
  'mortuary.settings.manage': 'Set up cold rooms, slots and the daily storage charge',
  'births.read':
    'See birth notifications, the birth register and print birth notification summaries',
  'births.record': "Record a live birth, creating the baby's record linked to the mother",
  'births.correct': 'Mark a birth notification entered in error, with a reason',
  // Maternity and MCH (docs/plans/phase2-maternity-mch.md §3.1).
  'maternity.read':
    'See pregnancies, ANC contacts, labour records, deliveries, postnatal checks and the labour ward list',
  'maternity.record':
    'Open and correct pregnancies, and record ANC contacts, labour observations, deliveries and postnatal checks, or mark them entered in error with a reason',
  'mch.read': "See a child's immunisations, due list and growth",
  'mch.record':
    'Record immunisation doses (from stock or from the card) and weight and height, or mark a dose entered in error with a reason',
  'maternity.settings.manage':
    'Set up maternity and MCH: danger-sign lists, partograph lines, the immunisation store, vaccines and the schedule',
  // Accounting integration and doctor revenue share
  // (docs/plans/phase3-accounting-and-revenue-share.md §4).
  'accounting.read': 'See the chart-of-accounts mapping, journals and closed periods',
  'accounting.manage': 'Map Wodi’s accounts to the organisation’s chart of accounts',
  'accounting.export': 'Download journals for the accounting package',
  'accounting.period.close':
    'Close an accounting period, freezing its journal; only for roles that use two-factor sign-in',
  'revenue_share.read': "See revenue share rules, settings and every practitioner's statements",
  'revenue_share.manage':
    'Set revenue share rules and whether shares are earned on billed or collected revenue',
  'revenue_share.prepare': 'Prepare revenue share statements and withdraw them before approval',
  'revenue_share.approve':
    'Approve or reject revenue share statements prepared by someone else, never your own',
  'revenue_share.payout.record': 'Record revenue share payouts made outside Wodi',
  'revenue_share.own.read': 'See your own revenue share statements',
  // Patient portal and feedback (docs/plans/phase3-portal-feedback-kiswahili.md §3.1).
  'portal.accounts.manage':
    "Enrol patients in the patient portal with their consent, link their children, and suspend or close a patient's access",
  'feedback.request.send': 'Send a patient an SMS link to rate a visit',
  'feedback.read': 'See patient feedback, its comments and trends',
  'feedback.respond': 'Reply to patient feedback and mark it resolved',
  'feedback.escalate': 'Escalate a complaint in patient feedback to an incident, with a reason',
  // Incident reporting (docs/plans/phase3-operations-modules.md §1.3).
  'incidents.report': 'Report incidents, and see the incidents they reported',
  'incidents.read':
    'See the incident register; anonymous reporters stay hidden unless they also manage incidents',
  'incidents.manage':
    'Review incidents, record root causes and actions, close, reopen or enter them in error, and change incident lists',
  // Infection control (docs/plans/phase3-operations-modules.md §2.3).
  'infection.flag':
    'Start and end isolation and precaution flags on a patient; they show on the banner and bed board',
  'infection.record': 'Record hospital-acquired infections on a stay, or enter one in error',
  'infection.read': 'See the infection line list and counts',
  'infection.manage': 'Change the precaution and infection category lists',
  // Assets and maintenance (docs/plans/phase3-operations-modules.md §3.2).
  'assets.read': 'See the asset register, maintenance due and work orders',
  'assets.manage':
    'Register assets, change their details, take them out of service or condemn them, and change asset categories',
  'assets.maintain':
    'Open, start, complete and cancel work orders, and record parts used from stock',
  // Roster and attendance (docs/plans/phase3-operations-modules.md §4.2).
  'roster.read': 'See rosters and ask to swap their own shifts',
  'roster.manage': 'Set shifts, build rosters, remove entries and approve or reject swaps',
  'attendance.clock': 'Clock themselves in and out at the facility',
  'attendance.read': 'See the attendance against roster report',
  // Diet orders and the kitchen (docs/plans/phase3-kitchen-and-housekeeping.md §1.3).
  'diet.order': 'Order, change or enter in error the diet of an inpatient',
  'kitchen.read':
    'See meal lists per ward and print meal tickets: names, beds, diets and allergies, never diagnoses',
  'kitchen.manage': 'Change the diet type and texture lists',
  // Housekeeping (docs/plans/phase3-kitchen-and-housekeeping.md §2.3).
  'housekeeping.read': 'See beds waiting for cleaning, area task lists and overdue tasks',
  'housekeeping.record':
    'Start and complete cleaning tasks, skip scheduled ones with a reason, and record consumables used from stock',
  'housekeeping.manage': 'Set up housekeeping areas and their cleaning schedules',
} as const;

export type Permission = keyof typeof PERMISSIONS;

export const PERMISSION_LIST = Object.keys(PERMISSIONS) as readonly Permission[];

export function isPermission(value: string): value is Permission {
  return Object.hasOwn(PERMISSIONS, value);
}

export interface RoleGrant {
  permissions: readonly string[];
  requires2fa: boolean;
}

export interface ResolvedAccess {
  permissions: ReadonlySet<Permission>;
  /** True if any held role requires two-factor authentication. */
  requires2fa: boolean;
}

/**
 * Combines the roles a user holds at one facility. Permissions are the union of the
 * roles' permissions; strings no longer in the list are ignored rather than trusted.
 */
export function resolveAccess(roles: readonly RoleGrant[]): ResolvedAccess {
  const permissions = new Set<Permission>();
  for (const role of roles) {
    for (const p of role.permissions) if (isPermission(p)) permissions.add(p);
  }
  return { permissions, requires2fa: roles.some(roleNeedsTwoFactor) };
}

export function hasAll(access: ResolvedAccess, required: readonly Permission[]): boolean {
  return required.every((p) => access.permissions.has(p));
}
